import useFileStore from '@/utils/fileStore';
import { App, Button, Input, InputRef, Modal, Radio, Select } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import FolderSelector from './folderSelector';
import { FileTypeEnum, FileTypeToExtensionMap } from '@/types/fileTypeEnum';
import { useTranslation } from 'react-i18next';

interface SaveFileDialogProps {
    visible: boolean;
    fileKey?: string;
    onClose: () => void;
}

const SaveFileDialog: React.FC<SaveFileDialogProps> = ({ visible, fileKey, onClose }) => {
    const { message } = App.useApp();
    const [fileName, setFileName] = useState<string | undefined>();
    const [filePath, setFilePath] = useState<string | undefined>();
    const [fileType, setFileType] = useState<FileTypeEnum>(FileTypeEnum.Mlatom);
    const [isOthersSelected, setIsOthersSelected] = useState<boolean>(false);
    const baseHome = useFileStore((state) => state.baseHome);
    const saveTempFile = useFileStore((state) => state.saveTempFile);
    const { t } = useTranslation('common');

    const inputRef = useRef<InputRef>(null);

    // Auto-update filename extension when file type changes
    useEffect(() => {
        if (fileName) {
            const suffix = FileTypeToExtensionMap[fileType]
            setFileName((prevName) => {
                if (!prevName) return prevName;
                // If previous name already has the correct suffix, do nothing
                if (prevName.endsWith(suffix)) {
                    return prevName;
                }
                // Remove any existing known suffix and add the new one
                const nameWithoutSuffix = prevName.replace(/(\.inp|\.py|\.txt|\.xyz|\.com)$/, '');
                return nameWithoutSuffix + suffix;
            });
        }
    }, [fileType, isOthersSelected]);

    const handleSave = async () => {
        if (fileName && fileKey) {
            let savePath = baseHome
            if (filePath) {
                savePath = filePath
            }
            console.log(savePath)
            if (!savePath) {
                message.error(t('save_file.dir_required'));
                return;
            }

            console.log(fileName, savePath, fileKey)
            const isSuccess = await saveTempFile(fileName, savePath, fileKey);
            if (isSuccess) {
                handleClose();
            }
        } else {
            message.error(t('save_file.name_required'));
        }
    };

    const handleClose = () => {
        onClose();
    }

    // auto focus on input
    useEffect(() => {
        if (visible) {
            // Set default filename with extension when dialog opens
            if (!fileName) {
                setFileName('.inp');
            }
            setTimeout(() => {
                inputRef.current?.focus({
                    cursor: 'start',
                });
            }, 100);
        } else {
            setFileName(undefined);
            setFilePath(undefined);
            setFileType(FileTypeEnum.Mlatom); // Reset to default
            setIsOthersSelected(false);
        }
    }, [visible])

    const handleRadioChange = (e: any) => {
        const value = e.target.value;
        if (value === 'others') {
            setIsOthersSelected(true);
            setFileType(FileTypeEnum.XYZ);
        } else {
            setIsOthersSelected(false);
            setFileType(value);
        }
    };

    return (
        <Modal
            title={t('save_file.title')}
            open={visible}
            destroyOnHidden
            onCancel={handleClose}
            width={600}
            footer={[
                <Button key="cancel" onClick={handleClose}>
                    {t('save_file.cancel')}
                </Button>,
                <Button key="save" type="primary" onClick={handleSave}>
                    {t('save_file.save')}
                </Button>,
            ]}
        >
            <div className='mb-2'>
                <Radio.Group value={isOthersSelected ? "others" : fileType} onChange={handleRadioChange}>
                    <Radio value="mlatom">{t('save_file.mlatom_input')}</Radio>
                    <Radio value="python">{t('save_file.python')}</Radio>
                    <Radio value="text">{t('save_file.text')}</Radio>
                    <Radio value="others">{t('save_file.others')}{isOthersSelected && <span>:</span>}</Radio>
                </Radio.Group>
                <div className="inline-block">
                    <Select
                        value={isOthersSelected?fileType:FileTypeEnum.XYZ}
                        disabled={!isOthersSelected}
                        onChange={e => setFileType(e as FileTypeEnum)}
                        style={{ width: 150 }}
                        options={[
                            { value: FileTypeEnum.XYZ, label: t('save_file.structure_xyz') },
                            { value: FileTypeEnum.GAUSSIAN, label: t('save_file.gaussian_input') },
                        ]}
                    />
                </div>
            </div>
            <Input
                placeholder={t('save_file.enter_name')}
                value={fileName}
                ref={inputRef}
                id="fileName-input"
                onChange={(e) => setFileName(e.target.value)}
                className=' mb-4'
            />
            <div className=' h-60 overflow-hidden'>
                <FolderSelector onSelectDirectory={setFilePath} initialPath={baseHome} />
            </div>
        </Modal>
    );
};

export default SaveFileDialog;